import { useEffect } from 'react';
import * as R from 'remeda';
import { createStoreHooks } from '../../store';
import type { CameraSlice } from '../types';

type Root = CameraSlice;

const { useStore: useSelector } = createStoreHooks<Root>();

/**
 * Observes container element and canvas content changes, updating dimensions.
 * - Viewport rect: Tracks HTML container resize and position (window/panel resize)
 * - Canvas dimensions: Tracks SVG content changes (elements added/removed/modified)
 */
export const useViewportDimensions = () => {
  const containerRef = useSelector(s => s.canvas.refs.containerRef);
  const canvasRef = useSelector(s => s.canvas.refs.contentRef); // We observe the content group
  const actions = useSelector(s => s.canvas.actions);
  const { setViewportRect, setCanvasDimensions } = actions;

  // Observe viewport (HTML container) resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return R.doNothing;
    }

    const updateViewportRect = (entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (entry) {
        const rect = entry.target.getBoundingClientRect();
        setViewportRect({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    const resizeObserver = new ResizeObserver(updateViewportRect);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, setViewportRect]);

  // Observe canvas content changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return R.doNothing;
    }

    const updateCanvasDimensions = () => {
      const bbox = canvas.getBBox();
      setCanvasDimensions({
        width: bbox.width || 600,
        height: bbox.height || 1000,
      });
    };

    // Measure initially
    updateCanvasDimensions();

    let rafId: number | null = null;

    // Observe DOM mutations (elements added/removed/modified)
    // But ignore overlay elements that shouldn't affect canvas dimensions
    const mutationObserver = new MutationObserver(mutations => {
      const shouldUpdate = mutations.some(mutation => {
        // Ignore mutations on overlay elements
        if (mutation.target instanceof Element) {
          if (
            mutation.target.hasAttribute('data-canvas-overlay') ||
            mutation.target.closest('[data-canvas-overlay]')
          ) {
            return false;
          }
        }
        return true;
      });

      if (shouldUpdate && rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          updateCanvasDimensions();
        });
      }
    });
    mutationObserver.observe(canvas, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'width',
        'height',
        'x',
        'y',
        'd',
        'points',
        'transform',
      ],
    });

    return () => {
      mutationObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [canvasRef, setCanvasDimensions]);
};
